import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BellRing } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

const OPTIONS = [
  { value: "email", label: "Email only" },
  { value: "sms", label: "Text message only" },
  { value: "both", label: "Email and text" },
];

export const ReminderPreferences = () => {
  const { user, setUser } = useAuth();
  const [phone, setPhone] = useState(user?.phone || "");
  const [pref, setPref] = useState(user?.reminder_pref || "email");
  const [smsEnabled, setSmsEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/notifications/config").then((r) => setSmsEnabled(r.data.sms_enabled)).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch("/me", { phone, reminder_pref: pref });
      setUser({ ...user, ...data });
      toast.success("Reminder preferences saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="reminder-preferences-card">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2"><BellRing className="w-5 h-5 text-primary" /> Appointment Reminders</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">We'll remind you the day before each confirmed appointment. Choose how you'd like to hear from us.</p>
        <div className="space-y-1.5 max-w-xs">
          <Label htmlFor="reminder-phone">Mobile number</Label>
          <Input id="reminder-phone" type="tel" placeholder="(385) 555-0100" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="reminder-phone-input" />
        </div>
        <div className="flex flex-wrap gap-2" data-testid="reminder-pref-options">
          {OPTIONS.map((o) => (
            <button key={o.value} type="button" onClick={() => setPref(o.value)} data-testid={`reminder-pref-${o.value}`}
              className={`text-sm px-4 py-2 rounded-full border transition-colors duration-200 ${pref === o.value ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary/40"}`}>
              {o.label}
            </button>
          ))}
        </div>
        {!smsEnabled && pref !== "email" && (
          <p className="text-xs text-muted-foreground" data-testid="sms-not-configured-note">Text reminders will start as soon as our office enables SMS — you'll still receive email reminders if selected.</p>
        )}
        <Button onClick={save} disabled={saving} data-testid="reminder-save-button">{saving ? "Saving…" : "Save Preferences"}</Button>
      </CardContent>
    </Card>
  );
};
